import { useMemo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../API/webserviceUrl";
import { Link } from "react-router-dom";

type AnswerType = "rating" | "text";
type Section = "criteria" | "general";

interface EvalAnswer {
  ques_no: number;
  section: Section;
  ques_key: string;
  label: string;
  description: string;
  weight: number;
  answer_type: AnswerType;
  rating_value: number | null;
  text_value: string | null;
}

interface Evaluation {
  evaluation_id: number;
  eval_by_group_id: number;
  evaluator_student_id: string;
  evaluator_name: string;
  score: string | number;
  is_final: boolean;
  submitted_on: string;
  answers: EvalAnswer[];
}

interface GroupEvaluationData {
  group_id: number;
  project_title: string;
  format_type: string;
  presentation?: string;
  evaluations_received: number;
  average_score: number;
  evaluations: Evaluation[];
}

function authHeaders(token: string | null) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function RatingScale({ value }: { value: number | null }) {
  const SCALE = [1, 2, 3, 4, 5];
  return (
    <div className="rating">
      <div className="rating__track" role="radiogroup" aria-label="rating">
        <span className="rating__rail" aria-hidden="true" />
        {SCALE.map((n) => (
          <button
            type="button"
            key={n}
            className={`rating__pip${value === n ? " rating__pip--active" : ""}`}
            aria-pressed={value === n}
            aria-label={`${n} of 5`}
            disabled
          >
            {n}
          </button>
        ))}
      </div>
      <div className="rating__ends">
        <span>Needs Improvement</span>
        <span>Excellent</span>
      </div>
    </div>
  );
}

function CriterionCard({ answer }: { answer: EvalAnswer }) {
  return (
    <div className="criterion">
      <div className="criterion__head">
        <h3>{answer.label}</h3>
        <span className="weight-badge">{answer.weight}%</span>
      </div>
      <p className="criterion__desc">{answer.description}</p>
      <RatingScale value={answer.rating_value} />
    </div>
  );
}

function TextAnswerField({ answer }: { answer: EvalAnswer }) {
  return (
    <div className="field">
      <label>{answer.label}</label>
      <textarea rows={4} value={answer.text_value ?? ""} disabled readOnly />
      {!answer.text_value && (
        <p className="field-error" style={{ color: "#9ca3af" }}>
          No response provided.
        </p>
      )}
    </div>
  );
}

export default function TrainerViewGroupEvaluation() {
  const token = localStorage.getItem("token");
  const { group_id: groupIdParam } = useParams<{ group_id: string }>();
  const selectedGroupId = groupIdParam ? Number(groupIdParam) : null;

  const [data, setData] = useState<GroupEvaluationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Which evaluation (evaluator) is currently shown, and which step within it
  const [activeEvalIndex, setActiveEvalIndex] = useState(0);
  const [step, setStep] = useState(0);

  const steps = ["Group Info", "Criteria Review", "General Evaluation"];
  const lastStep = steps.length - 1;

  const fetchGroupEvaluationDetails = async (groupId: number) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(
        `${BASE_URL}/group/evaluation-details/${groupId}`,
        authHeaders(token),
      );
      const resData: GroupEvaluationData | undefined = res?.data?.data;
      if (resData) setData(resData);
    } catch (e) {
      console.log(e, "ERROR coming from group-evaluation-details");
      setError("Could not load this group's evaluation. Please try again.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedGroupId) fetchGroupEvaluationDetails(selectedGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const activeEvaluation = data?.evaluations?.[activeEvalIndex] || null;

  const criteriaAnswers = useMemo(
    () => (activeEvaluation?.answers || []).filter((a) => a.section === "criteria"),
    [activeEvaluation],
  );
  const generalAnswers = useMemo(
    () => (activeEvaluation?.answers || []).filter((a) => a.section === "general"),
    [activeEvaluation],
  );

  function goNext() {
    setStep((s) => Math.min(s + 1, lastStep));
  }
  function goBack() {
    setStep((s) => Math.max(s - 1, 0));
  }

  return (
    <div className="page-wrapper">
      <div className="content kv-app mb-3">
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;0,700;1,500&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');

        html, body {
          overflow-x: hidden;
        }

        .kv-app {
          --ink: #2e7d32;
          --ink-soft: #55606E;
          --paper: #F6F3EC;
          --paper-raised: #FCFAF5;
          --line: #DFD8C8;
          --amber: #C98A3E;
          --amber-deep: #9C6428;
          --sage: #4B6455;
          --rust: #9C3D34;
          font-family: 'Inter', sans-serif;
          background: #fff;
          color: var(--ink);
          min-height: 100%;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          box-sizing: border-box;
          padding: clamp(16px, 4vw, 48px);
          display: flex;
          justify-content: center;
        }
        .kv-app *, .kv-app *::before, .kv-app *::after { box-sizing: border-box; }

        .kv-shell { width: 100%; max-width: 100%; }

        .kv-tag {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--amber-deep);
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }
        .kv-tag::after {
          content: "";
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        // .kv-header {
        //    margin-top: -30px;
        //    margin-left: -50px;
        //  }

        .kv-header h1 {
          font-family: "raboto", sans-serif;
          font-weight: 700;
          font-size: clamp(26px, 4vw, 34px);
          margin: 0 0 6px;
          line-height: 1.15;
        }
        .kv-header p {
          margin: 0 0 28px;
          font-style: italic;
          color: var(--ink-soft);
          font-size: 14.5px;
        }

        /* progress ledger */
        .kv-progress {
          display: flex;
          gap: 6px;
          margin-bottom: 10px;
        }
        .kv-progress__seg {
          flex: 1;
          height: 4px;
          background: var(--line);
          border-radius: 2px;
          position: relative;
          overflow: hidden;
        }
        .kv-progress__seg::after {
          content: "";
          position: absolute;
          inset: 0;
          background: var(--ink);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.35s ease;
        }
        .kv-progress__seg--done::after,
        .kv-progress__seg--current::after { transform: scaleX(1); }
        .kv-progress__seg--current::after { background: var(--amber); }

        .kv-progress-label {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--amber-deep);
          margin-bottom: 10px;
        }

        .kv-stepline {
          display: flex;
          justify-content: space-between;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ink-soft);
          margin-bottom: 30px;
          gap: 4px;
        }
        .kv-stepline span {
          flex: 1;
          text-align: center;
        }
        .kv-stepline span:first-child { text-align: left; }
        .kv-stepline span:last-child { text-align: right; }
        .kv-stepline span.active { color: var(--ink); font-weight: 600; }
        .kv-stepline span.done { color: var(--sage); }

        @media (max-width: 560px) {
          .kv-stepline span { display: none; }
          .kv-stepline span.active { display: block; flex: 1; text-align: center; }
        }

        .kv-card {
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: clamp(20px, 4vw, 36px);
          box-shadow: 0 1px 2px rgba(27, 42, 74, 0.04), 0 12px 28px -18px rgba(27, 42, 74, 0.25);
          max-width: 100%;
          overflow-wrap: break-word;
        }

        .kv-sec-title {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }
        .kv-sec-title h2 {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 21px;
          font-weight: 600;
          margin: 0;
        }
        .kv-sec-num {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.1em;
          color: var(--amber-deep);
          white-space: nowrap;
        }
        .kv-sec-sub {
          color: var(--ink-soft);
          font-size: 14px;
          margin: 6px 0 26px;
        }

        .note-block {
          background: #FBF3E5;
          border: 1px dashed #E1C08C;
          border-radius: 8px;
          padding: 14px 16px;
          font-size: 13.5px;
          color: #6E4A1F;
          line-height: 1.55;
          margin-bottom: 22px;
        }
        .note-block strong { color: var(--amber-deep); }

        .status-block {
          border-radius: 8px;
          padding: 14px 16px;
          font-size: 13.5px;
          line-height: 1.55;
          margin-bottom: 22px;
        }
        .status-block--info {
          background: #EEF3EE;
          color: #33513A;
          border: 1px dashed #9FBBA6;
        }
        .status-block--error {
          background: rgba(156,61,52,0.06);
          color: var(--rust);
          border: 1px dashed var(--rust);
        }

        .field { margin-bottom: 22px; }
        .field label {
          display: block;
          font-weight: 600;
          font-size: 14.5px;
          margin-bottom: 4px;
        }
        .field .req { color: var(--rust); }
        .field__hint { font-size: 12.5px; color: var(--ink-soft); margin: 0 0 8px; }
        .field input[type="text"], .field textarea {
          width: 100%;
          max-width: 100%;
          font-family: 'Inter', sans-serif;
          font-size: 14.5px;
          padding: 11px 13px;
          border: 1px solid var(--line);
          border-radius: 7px;
          background: #fff;
          color: var(--ink);
          resize: vertical;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .field input[type="text"]:focus, .field textarea:focus {
          outline: none;
          border-color: var(--amber);
          box-shadow: 0 0 0 3px rgba(201,138,62,0.18);
        }
        .field input:disabled, .field textarea:disabled {
          background: #F6F3EC;
          color: var(--ink-soft);
          cursor: not-allowed;
        }
        .field--error input, .field--error textarea { border-color: var(--rust); }
        .field-error { color: var(--rust); font-size: 12.5px; margin: 6px 0 0; }

        .type-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 8px;
        }
        @media (max-width: 640px) { .type-grid { grid-template-columns: 1fr; } }

        .type-option {
          border: 1px solid var(--line);
          background: #fff;
          border-radius: 8px;
          padding: 14px 16px;
          cursor: pointer;
          text-align: left;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
          transition: border-color .15s ease, background .15s ease;
        }
        .type-option:hover { border-color: var(--amber); }
        .type-option--active {
          border-color: var(--ink);
          background: var(--ink);
           color: #fff;
        }
        .type-option--disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .type-option-sub {
          display: block;
          font-size: 11.5px;
          font-weight: 400;
          margin-top: 4px;
          opacity: 0.8;
        }

        /* peer-group number pill grid */
        .pill-grid {
          display: grid;
          grid-template-columns: repeat(var(--cols, 4), 1fr);
          gap: 10px;
          margin-bottom: 8px;
        }
        @media (max-width: 480px) {
          .pill-grid { grid-template-columns: repeat(4, 1fr); }
        }
        .pill {
          border: 1px solid var(--line);
          background: #fff;
          border-radius: 8px;
          padding: 12px 0;
          cursor: pointer;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 14.5px;
          font-weight: 600;
          color: var(--ink);
          transition: all .15s ease;
        }
        .pill:hover { border-color: var(--amber); color: var(--amber-deep); }
        .pill--active {
          border-color: var(--ink);
          background: var(--ink);
          color: #fff;
        }

        .criterion {
          border-top: 1px solid var(--line);
          padding: 22px 0;
        }
        .criterion:first-of-type { border-top: none; padding-top: 0; }
        .criterion--error { background: rgba(156,61,52,0.04); border-radius: 8px; padding: 18px 16px; margin: 0 -16px; }
        .criterion__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .criterion__head h3 {
          font-family: 'Lora', serif;
          font-size: 16.5px;
          font-weight: 600;
          margin: 0;
        }
        .weight-badge {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11.5px;
          background: #EDE6D6;
          color: var(--amber-deep);
          padding: 3px 9px;
          border-radius: 20px;
          white-space: nowrap;
        }
        .criterion__desc {
          margin: 6px 0 16px;
          font-size: 13.5px;
          color: var(--ink-soft);
          line-height: 1.5;
        }

        .rating__track {
          position: relative;
          display: flex;
          justify-content: space-between;
          max-width: 320px;
        }
        .rating__rail {
          position: absolute;
          top: 50%;
          left: 18px;
          right: 18px;
          height: 1px;
          background: var(--line);
          transform: translateY(-50%);
          z-index: 0;
        }
        .rating__pip {
          position: relative;
          z-index: 1;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1.5px solid var(--line);
          background: #fff;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: var(--ink-soft);
          cursor: pointer;
          transition: all .15s ease;
        }
        .rating__pip:hover { border-color: var(--amber); color: var(--amber-deep); }
        .rating__pip--active {
          background: var(--ink);
          border-color: var(--ink);
          color: #fff;
          box-shadow: 0 0 0 4px rgba(201,138,62,0.22);
        }
        .rating__pip:disabled {
          cursor: not-allowed;
          opacity: 0.85;
        }
        .rating__ends {
          display: flex;
          justify-content: space-between;
          max-width: 320px;
          margin-top: 8px;
          font-size: 11px;
          color: var(--ink-soft);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        @media (max-width: 420px) {
          .rating__track, .rating__ends { max-width: 100%; }
          .rating__pip { width: 32px; height: 32px; }
        }

        .kv-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 30px;
          gap: 12px;
        }
        .btn {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          padding: 11px 22px;
          border-radius: 7px;
          border: 1px solid var(--ink);
          cursor: pointer;
          transition: all .15s ease;
        }
        .btn-ghost { background: transparent; color: var(--ink); }
        .btn-ghost:hover { background: rgba(27,42,74,0.06); }
        .btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }
        .btn-solid { background: var(--ink); color: #fff; }
        .btn-solid:hover { background: var(--amber-deep); border-color: var(--amber-deep); }
        .btn-solid:disabled { opacity: 0.5; cursor: not-allowed; }

        /* review + submit */
        .review-list { list-style: none; margin: 0 0 22px; padding: 0; }
        .review-list li {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 0;
          border-top: 1px solid var(--line);
          font-size: 13.5px;
        }
        .review-list li:first-child { border-top: none; }
        .review-list .rl-label { color: var(--ink-soft); }
        .review-list .rl-val { font-family: "Inter", "Segoe UI", Arial, sans-serif; font-weight: 600; }

        .score-panel {
          background: var(--ink);
          color: #fff;
          border-radius: 10px;
          padding: 22px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .score-panel .label {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #E2C89A;
        }
        .score-panel .num {
          font-family: 'Lora', serif;
          font-size: 40px;
          font-weight: 700;
        }

        .stamp-wrap {
          text-align: center;
          padding: 24px 0 4px;
        }
        .stamp {
          display: inline-block;
          border: 3px solid var(--sage);
          color: var(--sage);
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-weight: 600;
          letter-spacing: 0.14em;
          padding: 10px 22px;
          border-radius: 8px;
          transform: rotate(-3deg);
          margin-bottom: 18px;
        }
        .submitted-title {
          font-family: 'Lora', serif;
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
        }
        .submitted-sub { color: var(--ink-soft); font-size: 14px; margin: 0 0 26px; }

        /* ---------------- Responsive ---------------- */

        @media (max-width: 900px) {
          .kv-header {
            margin-left: -20px;
          }
        }

        @media (max-width: 700px) {
          .kv-app {
            padding: clamp(14px, 4vw, 28px);
          }

          .kv-header {
            margin-top: -10px;
            margin-left: 0;
          }

          .kv-card {
            padding: clamp(16px, 4vw, 24px);
          }

          .kv-sec-title h2 {
            font-size: 18px;
          }

          .kv-sec-sub {
            font-size: 13px;
            margin-bottom: 20px;
          }

          .criterion {
            padding: 18px 0;
          }

          .criterion--error {
            margin: 0;
            padding: 16px;
          }

          .criterion__head h3 {
            font-size: 15px;
          }

          .weight-badge {
            font-size: 10.5px;
          }

          .score-panel {
            padding: 18px 18px;
          }

          .score-panel .num {
            font-size: 32px;
          }

          .kv-progress-label,
          .kv-tag {
            font-size: 10px;
          }
        }

        @media (max-width: 480px) {
          .kv-nav { flex-direction: column-reverse; align-items: stretch; }
          .kv-nav .btn { width: 100%; text-align: center; }

          .kv-header h1 {
            font-size: 22px;
          }

          .kv-card {
            padding: 16px;
            border-radius: 8px;
          }

          .kv-sec-title {
            gap: 6px;
          }

          .kv-sec-num {
            font-size: 10px;
          }

          .type-option {
            padding: 12px 14px;
            font-size: 13.5px;
          }

          .pill {
            padding: 10px 0;
            font-size: 13px;
          }

          .note-block {
            padding: 12px 14px;
            font-size: 12.5px;
          }

          .review-list li {
            font-size: 12.5px;
          }

          .stamp {
            padding: 8px 18px;
            letter-spacing: 0.1em;
          }

          .submitted-title {
            font-size: 20px;
          }
        }

        @media (max-width: 380px) {
          .kv-header h1 {
            font-size: 19px;
          }

          .kv-card {
            padding: 12px;
          }

          .rating__pip {
            width: 30px;
            height: 30px;
            font-size: 12px;
          }

          .score-panel {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .score-panel .num {
            font-size: 28px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .kv-progress__seg::after {
            transition: none;
          }
        }

         .kv-progress {
          display: flex;
          gap: 6px;
          margin-bottom: 10px;
        }
        .kv-progress__seg {
          flex: 1;
          height: 4px;
          background: var(--line);
          border-radius: 2px;
          position: relative;
          overflow: hidden;
        }
        .kv-progress__seg::after {
          content: "";
          position: absolute;
          inset: 0;
          background: var(--ink);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.35s ease;
        }
        .kv-progress__seg--done::after,
        .kv-progress__seg--current::after { transform: scaleX(1); }
        .kv-progress__seg--current::after { background: var(--amber); }

        .kv-progress-label {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--amber-deep);
          margin-bottom: 10px;
        }

        .kv-stepline {
          display: flex;
          justify-content: space-between;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ink-soft);
          margin-bottom: 30px;
          gap: 4px;
        }
        .kv-stepline span {
          flex: 1;
          text-align: center;
        }
        .kv-stepline span:first-child { text-align: left; }
        .kv-stepline span:last-child { text-align: right; }
        .kv-stepline span.active { color: var(--ink); font-weight: 600; }
        .kv-stepline span.done { color: var(--sage); }

        @media (max-width: 560px) {
          .kv-stepline span { display: none; }
          .kv-stepline span.active { display: block; flex: 1; text-align: center; }
        }

      `}</style>
        <div className="kv-shell">
          <div className="kv-header">
            <h1>Group Evaluation — Trainer View</h1>
          </div>

          {loading && <p className="field__hint">Loading evaluation details…</p>}
          {error && <div className="status-block status-block--error">{error}</div>}

          {!loading && !error && data && (
            <>
              {data.evaluations.length === 0 && (
                <div className="status-block status-block--info">
                  No finalized evaluations yet for this group.
                </div>
              )}

              {data.evaluations.length > 0 && (
                <>
                  {data.evaluations.length > 1 && (
                    <div className="type-grid" style={{ marginBottom: "20px" }}>
                      {data.evaluations.map((ev, i) => (
                        <button
                          type="button"
                          key={ev.evaluation_id}
                          className={`type-option${i === activeEvalIndex ? " type-option--active" : ""}`}
                          onClick={() => {
                            setActiveEvalIndex(i);
                            setStep(0);
                          }}
                        >
                          {ev.evaluator_name}
                          <span className="type-option-sub">Score: {ev.score}/100</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="kv-progress" style={{ marginTop: "10px" }}>
                    {steps.map((_, i) => (
                      <div
                        key={i}
                        className={`kv-progress__seg ${i < step
                          ? "kv-progress__seg--done"
                          : i === step
                            ? "kv-progress__seg--current"
                            : ""
                          }`}
                      />
                    ))}
                  </div>
                  <div className="kv-progress-label">
                    Step {step + 1} of {steps.length}
                  </div>
                  <div className="kv-stepline">
                    {steps.map((s, i) => (
                      <span
                        key={s}
                        className={i === step ? "active" : i < step ? "done" : ""}
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="kv-card">
                    {step === 0 && (
                      <>
                        <div className="kv-sec-title">
                          <h2>Group Info</h2>
                          <span className="kv-sec-num">SEC 01 / 03</span>
                        </div>
                        <div className="field">
                          <label>Project Title</label>
                          <p className="field__hint">{data.project_title}</p>
                        </div>
                        <div className="field">
                          <label>Project Type</label>
                          <p className="field__hint">
                            {data.format_type === "design_synthesis"
                              ? "Design Synthesis"
                              : data.format_type === "algorithm_development"
                                ? "Algorithm Development"
                                : "Literature search"}
                          </p>
                        </div>
                        {activeEvaluation && (
                          <div className="status-block status-block--info">
                            Evaluated by {activeEvaluation.evaluator_name} on{" "}
                            {new Date(activeEvaluation.submitted_on).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }).replace(",", "")}{" "}
                            — score: {activeEvaluation.score}/100
                          </div>
                        )}
                      </>
                    )}

                    {step === 1 && (
                      <>
                        <div className="kv-sec-title">
                          <h2>Criteria Review</h2>
                          <span className="kv-sec-num">SEC 02 / 03</span>
                        </div>
                        {criteriaAnswers.map((a) =>
                          a.answer_type === "text" ? (
                            <TextAnswerField key={a.ques_no} answer={a} />
                          ) : (
                            <CriterionCard key={a.ques_no} answer={a} />
                          ),
                        )}
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <div className="kv-sec-title">
                          <h2>General Evaluation</h2>
                          <span className="kv-sec-num">SEC 03 / 03</span>
                        </div>
                        {generalAnswers.map((a) =>
                          a.answer_type === "text" ? (
                            <TextAnswerField key={a.ques_no} answer={a} />
                          ) : (
                            <CriterionCard key={a.ques_no} answer={a} />
                          ),
                        )}
                      </>
                    )}

                    <div className="kv-nav">
                      {step === 0 ? (
                        <Link to={"/view-assessments"} className="btn btn-ghost">
                          ← Back to Groups
                        </Link>
                      ) : (
                        <button className="btn btn-ghost" onClick={goBack} disabled={step === 0}>
                          ← Back
                        </button>
                      )}
                      {step < lastStep && (
                        <button className="btn btn-solid" onClick={goNext}>
                          Continue →
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}